/**
 * API Client
 * Centralized API calls with authentication
 */

import { useAuthStore } from "../stores/auth-store";
import { keepRead, offlineRead } from "../offline/reads";
import { isNetworkError, markReachable, markUnreachable } from "../offline/status";
import type {
  Booking,
  BookingArticle,
  Trip,
  DaySchedule,
  Partner,
  AllowedDomain,
  User,
  EmailTemplate,
  EmailTemplateType,
  CreateBookingRequest,
  BookingFilter,
  ScheduleFilter,
  ApiResponse,
  FortnoxCompanyInfo,
  ContactSubmission,
  TripPatch,
  DepartureLocation,
  TripImageAssetWithUsage,
  MyPartner,
  CompanyDetails,
  TripReviewRequest,
  TripTranslationRequest,
  TripTranslationResult,
  TripReviewResult,
  TenantHealthOverview,
  OpsHealthResponse,
} from "@common/types";

// Use environment variable for API URL, fallback to relative /api for local dev
const API_BASE = import.meta.env.VITE_API_URL || "/api";

/** Admin-only, read-only configuration and data completeness for the signed-in tenant. */
export const getTenantSetupHealth = () => apiRequest<ApiResponse<TenantHealthOverview>>("/tenant-health");

/** Admin-only runtime and infrastructure health. */
export const getOpsHealth = () => apiRequest<ApiResponse<OpsHealthResponse>>("/ops-health");

import type { ApprovedRoutine, EquipmentGroup, ExtractedSection, RoutineMappingRun, RoutineDocument, RoutineImportCandidate, RoutineImportSession, RoutineReviewRow, RoutineWorkspace, SupersededRoutine } from "@common/types";
const routineRoot = "/logbook/routine-workspace";
export const getRoutineWorkspace = (vesselId: string) => apiRequest<ApiResponse<RoutineWorkspace>>(`${routineRoot}/overview?vesselId=${encodeURIComponent(vesselId)}`);
export const setRoutineStatus = (vesselId: string, routineId: string, status: "active" | "retired") => apiRequest<ApiResponse<{ status: string }>>(`${routineRoot}/routines/${encodeURIComponent(routineId)}/status?vesselId=${encodeURIComponent(vesselId)}`, { method: "POST", body: JSON.stringify({ status }) });
export const importRoutineDocument = (form: FormData) => apiRequest<ApiResponse<RoutineImportSession>>(`${routineRoot}/import`, { method: "POST", body: form });
export const saveRoutineImport = (id: string, expectedRevision: number, candidates: RoutineImportCandidate[]) => apiRequest<ApiResponse<RoutineImportSession>>(`${routineRoot}/imports/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify({ expectedRevision, candidates }) });
export const reviewRoutineImport = (id: string) => apiRequest<ApiResponse<RoutineReviewRow[]>>(`${routineRoot}/imports/${encodeURIComponent(id)}/preview`);
export const publishRoutineImport = (id: string, expectedRevision: number) => apiRequest<ApiResponse<RoutineImportSession>>(`${routineRoot}/imports/${encodeURIComponent(id)}/publish`, { method: "POST", body: JSON.stringify({ expectedRevision }) });
export interface RoutineMappingPrompts { mapping: { version: string; system: string; schema: unknown }; links: { version: string; system: string; schema: unknown }; effort: { mapping: string | null; links: string | null }; deployment: string | null }
export const getRoutineMappingPrompts = () => apiRequest<ApiResponse<RoutineMappingPrompts>>(`${routineRoot}/mapping/prompt`);
export const discardRoutineDraft = (vesselId: string, id: string) => apiRequest<ApiResponse<{ discarded: number }>>(`${routineRoot}/imports/${encodeURIComponent(id)}?vesselId=${encodeURIComponent(vesselId)}`, { method: "DELETE" });
export const discardRoutineDrafts = (vesselId: string) => apiRequest<ApiResponse<{ discarded: number }>>(`${routineRoot}/drafts/all?vesselId=${encodeURIComponent(vesselId)}`, { method: "DELETE" });
export const getRoutineDocuments = (vesselId: string) => apiRequest<ApiResponse<RoutineDocument[]>>(`${routineRoot}/documents?vesselId=${encodeURIComponent(vesselId)}`);
export const getRoutineImportSections = (id: string) => apiRequest<ApiResponse<ExtractedSection[]>>(`${routineRoot}/imports/${encodeURIComponent(id)}/sections`);
export const getSupersededRoutines = (id: string) => apiRequest<ApiResponse<SupersededRoutine[]>>(`${routineRoot}/imports/${encodeURIComponent(id)}/superseded`);
export const getRoutineMapping = (id: string) => apiRequest<ApiResponse<RoutineMappingRun | null>>(`${routineRoot}/imports/${encodeURIComponent(id)}/mapping`);
export const startRoutineMapping = (id: string, sectionKeys?: string[]) => apiRequest<ApiResponse<RoutineMappingRun>>(`${routineRoot}/imports/${encodeURIComponent(id)}/mapping/start`, { method: "POST", body: JSON.stringify(sectionKeys ? { sectionKeys } : {}) });
export const stepRoutineMapping = (id: string, runId: string, expectedRevision: number) => apiRequest<ApiResponse<{ run: RoutineMappingRun; session: RoutineImportSession }>>(`${routineRoot}/imports/${encodeURIComponent(id)}/mapping/${encodeURIComponent(runId)}/step`, { method: "POST", body: JSON.stringify({ expectedRevision }) });
export const getRoutineEquipmentChanges = (id: string) => apiRequest<ApiResponse<{ newEquipment: { id: string; name: string }[]; since?: string }>>(`${routineRoot}/imports/${encodeURIComponent(id)}/mapping/equipment-changes`);
export const startRoutineLinks = (id: string) => apiRequest<ApiResponse<RoutineMappingRun>>(`${routineRoot}/imports/${encodeURIComponent(id)}/mapping/links`, { method: "POST", body: "{}" });
export const suggestEquipmentGroups = (id: string) => apiRequest<ApiResponse<{ groups: EquipmentGroup[] }>>(`${routineRoot}/imports/${encodeURIComponent(id)}/equipment-groups/suggest`, { method: "POST", body: "{}" });
export const saveEquipmentGroups = (id: string, expectedRevision: number, groups: EquipmentGroup[]) => apiRequest<ApiResponse<RoutineImportSession>>(`${routineRoot}/imports/${encodeURIComponent(id)}/equipment-groups`, { method: "PUT", body: JSON.stringify({ expectedRevision, groups }) });
export const replayRoutineImport =(id: string, expectedRevision: number) => apiRequest<ApiResponse<RoutineImportSession>>(`${routineRoot}/imports/${encodeURIComponent(id)}/replay`, { method: "POST", body: JSON.stringify({ expectedRevision }) });
export const getRoutineHistory =(vesselId: string, routineId: string) => apiRequest<ApiResponse<ApprovedRoutine[]>>(`${routineRoot}/history?vesselId=${encodeURIComponent(vesselId)}&routineId=${encodeURIComponent(routineId)}`);
export async function downloadRoutineDocument(vesselId: string, format: "docx" | "json" | "csv", importId?: string, original = false): Promise<void> {
  const endpoint = original && importId ? `${routineRoot}/imports/${encodeURIComponent(importId)}/original` : `${routineRoot}/export?vesselId=${encodeURIComponent(vesselId)}&format=${format}${importId ? `&importId=${encodeURIComponent(importId)}` : ""}`;
  const request = () => fetch(`${API_BASE}${endpoint}`, { headers: { Authorization: `Bearer ${useAuthStore.getState().token}` } });
  let response = await request();
  if (response.status === 401 && useAuthStore.getState().refreshToken) {
    const refreshed = await refreshAccessToken(useAuthStore.getState().refreshToken!);
    useAuthStore.getState().setToken(refreshed.token); response = await request();
  }
  if (!response.ok) { const data = await response.json().catch(() => ({})); throw new ApiError(data.error || `Download failed (${response.status})`, data.code, data.details, response.status); }
  const url = URL.createObjectURL(await response.blob()); const link = document.createElement("a");
  link.href = url; link.download = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] || `seaslot-${original ? "original" : "routines"}.${format}`;
  document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Make authenticated API request
 */
import { ApiError, NoConnectionError } from "./api-error";
import { available } from "../offline/db";
import { emitQueued } from "../offline/events";
import { enqueue, newOperationId, type QueueItem, type QueueKind } from "../offline/queue";
import { getStatus } from "../offline/status";
import { setSender } from "../offline/queue-state";
export { ApiError };

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetryAfterRefresh = false,
): Promise<T> {
  const token = useAuthStore.getState().token;

  // A file upload (FormData) sets its own multipart Content-Type with the boundary
  const isForm = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isForm ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  const isRead = !options.method || options.method === "GET";
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    // the server cannot be reached: a read can be answered from the copy kept on a prepared device
    const userId = useAuthStore.getState().user?.id;
    const copy = isRead && userId ? await offlineRead<Record<string, unknown>>(userId, endpoint) : undefined;
    markUnreachable(copy ? { at: copy.at, expired: copy.expired } : undefined);
    if (copy) return { ...(copy.data as object), offline: { at: copy.at, expired: copy.expired } } as T;
    throw new NoConnectionError();
  }
  markReachable();

  if (response.status === 401) {
    const refreshToken = useAuthStore.getState().refreshToken;

    // Try a single silent refresh before giving up, unless this request
    // *was* the retry (or the refresh call itself) to avoid looping.
    if (!isRetryAfterRefresh && refreshToken && endpoint !== "/auth/refresh") {
      try {
        const { token: newToken } = await refreshAccessToken(refreshToken);
        useAuthStore.getState().setToken(newToken);
        return apiRequest<T>(endpoint, options, true);
      } catch {
        useAuthStore.getState().logout();
        throw new Error("Session expired. Please login again.");
      }
    }

    useAuthStore.getState().logout();
    throw new Error("Session expired. Please login again.");
  }

  // Handle empty responses
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new ApiError(data.error || `API request failed (${response.status})`, data.code, data.details, response.status);
  }

  // keep a copy of the logbook answers when this device has been prepared for offline use
  if (isRead) {
    const userId = useAuthStore.getState().user?.id;
    if (userId) void keepRead(userId, endpoint, data);
  }

  return data;
}

// ============================================
// WRITING THAT CAN WAIT FOR A CONNECTION
// ============================================

export interface Queued {
  /** The request could not be sent now: it is stored on the device and will be sent later */
  queued?: boolean;
  operationId?: string;
}

/**
 * A write the skipper can make without a connection. It is sent with an operation id (the server
 * carries it out once). When the server cannot be reached the request is stored on the device with
 * the same id and sent later; the answer then says `queued`. If it cannot be stored either, this
 * throws: the entry is not saved and the person is told.
 */
async function writeOrQueue<T>(spec: { kind: QueueKind; label: string; endpoint: string; body: Record<string, unknown>; groupKey: string }): Promise<T & Queued> {
  const operationId = newOperationId();
  const userId = useAuthStore.getState().user?.id;
  // the time it happened is the time the person acted, not the time it is sent
  const body = { ...spec.body, occurredAt: spec.body.occurredAt ?? new Date().toISOString() };
  const store = async (): Promise<T & Queued> => {
    if (!userId) throw new Error("Log in to save entries");
    await enqueue({ operationId, userId, kind: spec.kind, label: spec.label, endpoint: spec.endpoint, body, groupKey: spec.groupKey });
    emitQueued();
    return { success: true, queued: true, operationId } as unknown as T & Queued;
  };
  if (getStatus().offline && available()) return store();
  try {
    return (await apiRequest<T>(spec.endpoint, { method: "POST", body: JSON.stringify(body), headers: { "X-Operation-Id": operationId } })) as T & Queued;
  } catch (e) {
    if (e instanceof NoConnectionError && available()) return store();
    throw e;
  }
}

/** Send one queued entry (the server carries it out once per operation id). */
async function sendQueued(item: QueueItem): Promise<unknown> {
  return apiRequest<unknown>(item.endpoint, { method: "POST", body: JSON.stringify(item.body), headers: { "X-Operation-Id": item.operationId } });
}
setSender(sendQueued);

// ============================================
// AUTH API
// ============================================

export async function loginWithOAuth(
  provider: "google" | "microsoft",
  accessToken: string,
) {
  return apiRequest<{
    success: boolean;
    data: {
      token: string;
      refreshToken?: string;
      user: {
        id: string;
        email: string;
        name: string;
        role: string;
        partnerId?: string;
        partnerName?: string;
      };
    };
  }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ provider, accessToken }),
  });
}

/**
 * Exchange the stored refresh token for a new access token.
 * Used by apiRequest's 401 handler to retry once before logging out.
 */
export async function refreshAccessToken(refreshToken: string) {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : {};

  if (!response.ok || !data.success) {
    throw new Error(data.error || "Failed to refresh session");
  }

  return data.data as { token: string };
}

export async function registerPartner(
  provider: "google" | "microsoft",
  accessToken: string,
  domain: string,
  name: string,
) {
  return apiRequest<ApiResponse<{ id: string }>>("/auth/register", {
    method: "POST",
    body: JSON.stringify({ provider, accessToken, domain, name }),
  });
}

export async function getCurrentUser() {
  return apiRequest<ApiResponse<{ id: string; email: string; role: string }>>(
    "/auth/me",
  );
}

// ============================================
// BOOKINGS API
// ============================================

export async function createBooking(booking: CreateBookingRequest) {
  return apiRequest<ApiResponse<Booking>>("/bookings", {
    method: "POST",
    body: JSON.stringify(booking),
  });
}

export async function getBookings(filter?: BookingFilter) {
  const params = new URLSearchParams();
  if (filter?.startDate) params.set("startDate", filter.startDate);
  if (filter?.endDate) params.set("endDate", filter.endDate);
  if (filter?.status) params.set("status", filter.status);
  if (filter?.tripId) params.set("tripId", filter.tripId);
  if (filter?.availabilityOnly) params.set("availabilityOnly", "true");

  const query = params.toString();
  return apiRequest<ApiResponse<Booking[]>>(
    `/bookings${query ? `?${query}` : ""}`,
  );
}

export async function getBookingById(id: string, date: string) {
  return apiRequest<ApiResponse<Booking>>(`/bookings/${id}?date=${date}`);
}

export async function getBookingHistory(id: string, date: string) {
  return apiRequest<ApiResponse<DataAuditLog[]>>(
    `/bookings/${id}/history?date=${date}`,
  );
}

export async function lookupBooking(confirmationCode: string) {
  return apiRequest<ApiResponse<Booking>>(
    `/bookings/lookup/${confirmationCode}`,
  );
}

export async function cancelBooking(id: string, date: string, notes?: string) {
  return apiRequest<ApiResponse<Booking>>(
    `/bookings/${id}/cancel?date=${date}`,
    {
      method: "POST",
      body: JSON.stringify({ notes }),
    },
  );
}

export async function updateBookingStatus(
  id: string,
  date: string,
  status: string,
  notes?: string,
  invoiceNumber?: string,
  fortnoxInvoiceTotal?: number,
) {
  return apiRequest<ApiResponse<Booking>>(
    `/bookings/${id}/status?date=${date}`,
    {
      method: "PATCH",
      body: JSON.stringify({ status, notes, invoiceNumber, fortnoxInvoiceTotal }),
    },
  );
}

export async function restoreBooking(id: string, date: string, notes?: string) {
  return apiRequest<ApiResponse<Booking>>(
    `/bookings/${id}/restore?date=${date}`,
    {
      method: "POST",
      body: JSON.stringify({ notes }),
    },
  );
}

export async function updateBookingDate(
  id: string,
  currentDate: string,
  newDate: string,
  newTimeSlot?: string,
  customStartTime?: string,
  notes?: string,
  extraCost?: number,
  extraCostDescription?: string,
  includeLunch?: boolean,
  numberOfGuests?: number,
  customDurationHours?: number,
  articles?: BookingArticle[],
  partnerId?: string,
  bookingReference?: string,
  customer?: { name?: string; country?: string; mobile?: string; email?: string },
  tripPrice?: number,
  suppressEmailNotification?: boolean,
  tripId?: string,
  departureLocationId?: string | null,
  expectedUpdatedAt?: string,
  overrideOverlap?: boolean,
) {
  // Build request body with only defined fields to avoid sending admin-only fields
  const body: any = { newDate };

  if (newTimeSlot !== undefined) body.newTimeSlot = newTimeSlot;
  if (notes !== undefined) body.notes = notes;
  if (includeLunch !== undefined) body.includeLunch = includeLunch;
  if (numberOfGuests !== undefined) body.numberOfGuests = numberOfGuests;

  // Admin-only fields - only include if explicitly provided
  if (customStartTime !== undefined) body.customStartTime = customStartTime;
  if (customDurationHours !== undefined)
    body.customDurationHours = customDurationHours;
  if (extraCost !== undefined) body.extraCost = extraCost;
  if (extraCostDescription !== undefined)
    body.extraCostDescription = extraCostDescription;
  if (articles !== undefined) body.articles = articles;
  if (partnerId !== undefined) body.partnerId = partnerId;
  if (bookingReference !== undefined) body.bookingReference = bookingReference;
  if (customer !== undefined) body.customer = customer;
  if (tripPrice !== undefined) body.tripPrice = tripPrice;
  if (suppressEmailNotification) body.suppressEmailNotification = true;
  if (tripId !== undefined) body.tripId = tripId;
  if (departureLocationId !== undefined) body.departureLocationId = departureLocationId; // null removes it
  if (overrideOverlap) body.overrideOverlap = true; // admin: save although the vessel is busy
  if (expectedUpdatedAt) body.expectedUpdatedAt = expectedUpdatedAt; // refused if the booking changed since it was loaded

  return apiRequest<ApiResponse<Booking>>(
    `/bookings/${id}/date?date=${currentDate}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

// ============================================
// TRIPS API
// ============================================

export async function getTrips() {
  return apiRequest<ApiResponse<Trip[]>>("/trips");
}

export async function getTripById(id: string) {
  return apiRequest<ApiResponse<Trip>>(`/trips/${id}`);
}

export async function createTrip(trip: Partial<Trip>) {
  return apiRequest<ApiResponse<Trip>>("/trips", {
    method: "POST",
    body: JSON.stringify(trip),
  });
}

/**
 * Update a trip. Omitted fields are unchanged; a field set to `null` is
 * removed from the trip (JSON drops `undefined`, so null is how to clear).
 */
export async function updateTrip(id: string, updates: TripPatch, expectedUpdatedAt?: string) {
  return apiRequest<ApiResponse<Trip>>(`/trips/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...updates, ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}) }),
  });
}

export async function getTripContentHistory(id: string) {
  return apiRequest<ApiResponse<import("@common/types").TripContentRevision[]>>(`/trips/${id}/content-history`);
}

export async function restoreTripContentRevision(id: string, revisionId: string, expectedCurrentRevisionId: string | undefined, expectedUpdatedAt: string) {
  return apiRequest<ApiResponse<Trip>>(`/trips/${id}/content-history/${revisionId}/restore`, {
    method: "POST",
    body: JSON.stringify({ expectedCurrentRevisionId, expectedUpdatedAt }),
  });
}

/** Switch a trip on or off for booking. (Not a field of the general update: it has its own endpoint.) */
export async function setTripActive(id: string, isActive: boolean) {
  return apiRequest<ApiResponse<Trip>>(`/trips/${id}/active`, {
    method: "PATCH",
    body: JSON.stringify({ isActive }),
  });
}

/** Archive or restore a trip. (Not a field of the general update: it has its own endpoint.) */
export async function setTripArchived(id: string, isArchived: boolean) {
  return apiRequest<ApiResponse<Trip>>(`/trips/${id}/archive`, {
    method: "PATCH",
    body: JSON.stringify({ isArchived }),
  });
}

/** What the library knows about an image that was just stored. */
export interface StoredTripImage {
  url: string;
  id?: string;
  title?: string;
}

/** Read-only editorial review of a draft; suggestions are applied in the wizard. */
export async function reviewTripDraft(input: TripReviewRequest, signal?: AbortSignal): Promise<TripReviewResult> {
  const response = await apiRequest<ApiResponse<TripReviewResult>>("/trip-review", {
    method: "POST", body: JSON.stringify(input), signal,
  });
  if (!response.data) throw new Error("The trip review could not be loaded");
  return response.data;
}

/** Translate explicit source text; the proposal is reviewed before saving a trip. */
export async function translateTripDraft(input: TripTranslationRequest, signal?: AbortSignal): Promise<TripTranslationResult> {
  const response = await apiRequest<ApiResponse<TripTranslationResult>>("/trip-translate", {
    method: "POST", body: JSON.stringify(input), signal,
  });
  if (!response.data) throw new Error("The translation could not be loaded");
  return response.data;
}

/**
 * Store a trip image: either an image URL (downloaded by the server) or an
 * uploaded image as a data URL, optionally with a library title, description
 * and tags. The image is recorded in the image library.
 */
export async function storeTripImage(
  source: ({ url: string } | { dataUrl: string } | { file: File }) & { title?: string; alt?: string; tags?: string[]; autoTag?: boolean },
): Promise<StoredTripImage> {
  let body: FormData | string;
  if ("file" in source) {
    body = new FormData(); body.append("file", source.file); body.append("title", source.title || source.file.name);
    if (source.autoTag) body.append("autoTag", "true");
  } else body = JSON.stringify(source);
  const response = await apiRequest<ApiResponse<StoredTripImage>>("/trip-images", {
    method: "POST",
    body,
  });
  if (!response.data?.url) throw new Error("The image could not be stored");
  return response.data;
}

/** Like storeTripImage, returning only the URL to save on the trip. */
export async function uploadTripImage(
  source: { url: string } | { dataUrl: string } | { file: File },
): Promise<string> {
  return (await storeTripImage({ ...source, autoTag: true })).url;
}

export const optimizeLibraryImage = (id: string) => apiRequest<ApiResponse<TripImageAssetWithUsage>>(`/images/${encodeURIComponent(id)}/optimize`, { method: "POST" });
export const classifyLibraryImage = (id: string) => apiRequest<ApiResponse<TripImageAssetWithUsage>>(`/images/${encodeURIComponent(id)}/classify`, { method: "POST" });
export interface AiPromptDetails { version: string; system: string; deployment: string | null; effort: string; profiles?: Record<string, string>; audiences?: Record<string, string> }
export const getAiPrompt = (kind: "trip-review" | "image-classification") => apiRequest<ApiResponse<AiPromptDetails>>(`/ai-prompts/${kind}`);
export async function downloadImageOriginal(id: string) {
  const request = () => fetch(`${API_BASE}/images/${encodeURIComponent(id)}/original`, { headers: { Authorization: `Bearer ${useAuthStore.getState().token}` } });
  let response = await request();
  if (response.status === 401 && useAuthStore.getState().refreshToken) {
    const refreshed = await refreshAccessToken(useAuthStore.getState().refreshToken!);
    useAuthStore.getState().setToken(refreshed.token); response = await request();
  }
  if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.error || "The original could not be downloaded"); }
  const url = URL.createObjectURL(await response.blob()); const link = document.createElement("a");
  link.href = url; link.download = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] || `original-${id}`;
  document.body.append(link); link.click(); link.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ============================================
// DEPARTURE LOCATIONS API
// ============================================

export interface LocationInput {
  name: string;
  address?: string | null;
  mapUrl?: string | null;
  notes?: string | null;
  /** A confirmed position; the server adds who confirmed it and when. null removes it. */
  coordinates?: { latitude: number; longitude: number; source: "coordinates" | "maps_url" | "map_pick"; mapsUrl?: string } | null;
  isActive?: boolean;
  sortOrder?: number | null;
}

export async function getLocations() {
  return apiRequest<ApiResponse<DepartureLocation[]>>("/locations");
}

export async function createLocation(input: LocationInput) {
  return apiRequest<ApiResponse<DepartureLocation>>("/locations", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Change an address. A null field is removed. Trips using the address are updated too (`updatedTrips`). */
export async function updateLocation(id: string, patch: Partial<LocationInput>) {
  return apiRequest<ApiResponse<DepartureLocation> & { updatedTrips: number }>(`/locations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** Delete an address. The server refuses (409) while any trip uses it. */
export async function deleteLocation(id: string) {
  return apiRequest<ApiResponse<void>>(`/locations/${id}`, { method: "DELETE" });
}

// ============================================
// IMAGE LIBRARY API
// ============================================

export interface ImageLibraryResponse {
  success: boolean;
  data: TripImageAssetWithUsage[];
  /** False in local development, where images are not stored. */
  storageConfigured: boolean;
}

export interface PageImageCandidate {
  url: string;
  alt?: string;
  inLibrary: boolean;
}

export async function getImages() {
  return apiRequest<ImageLibraryResponse>("/images");
}

export async function updateImage(
  id: string,
  patch: { title?: string; alt?: string | null; tags?: string[] },
) {
  return apiRequest<ApiResponse<TripImageAssetWithUsage>>(`/images/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** Delete an image and its file. The server refuses (409, naming the trips) while a trip uses it. */
export async function deleteImage(id: string) {
  return apiRequest<ApiResponse<void>>(`/images/${id}`, { method: "DELETE" });
}

/** Add files that are in storage but not in the library. */
export async function syncImages() {
  return apiRequest<ApiResponse<{ added: number; total: number }>>("/images/sync", { method: "POST", body: "{}" });
}

/** List the photos on a web page (https) so some can be imported into the library. */
export async function importPageImages(url: string) {
  return apiRequest<ApiResponse<{ pageUrl: string; images: PageImageCandidate[] }>>("/images/import-page", {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

// ============================================
// MY AGENCY API (branding printed on the agency's trip PDFs)
// ============================================

/** The caller's own partner; null for an admin or a user without a partner. */
export async function getMyPartner() {
  return apiRequest<ApiResponse<MyPartner | null>>("/me/partner");
}

/** Set the agency's website link (www.agency.se is fine); null or empty removes it. */
export async function updateMyWebsite(websiteUrl: string | null) {
  return apiRequest<ApiResponse<MyPartner>>("/me/partner", {
    method: "PATCH",
    body: JSON.stringify({ websiteUrl }),
  });
}

/** Upload or replace the agency logo (a PNG/JPEG/WebP data URL, max 2 MB). */
export async function uploadMyLogo(dataUrl: string) {
  return apiRequest<ApiResponse<MyPartner>>("/me/partner/logo", {
    method: "POST",
    body: JSON.stringify({ dataUrl }),
  });
}

export async function deleteMyLogo() {
  return apiRequest<ApiResponse<MyPartner>>("/me/partner/logo", { method: "DELETE" });
}

// ============================================
// COMPANY DETAILS API (printed on admin trip PDFs)
// ============================================

/** The company's own details; an empty name before anything was saved. */
export async function getCompany() {
  return apiRequest<ApiResponse<CompanyDetails>>("/company");
}

/** Admin: change fields; null or empty removes an optional one. */
export async function updateCompany(patch: Partial<Record<keyof CompanyDetails, string | null>>) {
  return apiRequest<ApiResponse<CompanyDetails>>("/company", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function reviewCompanyCancellationPolicy(language: "sv" | "en", text: string) {
  return apiRequest<ApiResponse<{ status: "completed" | "unavailable"; suggestion?: string; notes?: string[] }>>("/company/cancellation-policy/review", {
    method: "POST",
    body: JSON.stringify({ language, text }),
  });
}

export async function uploadCompanyLogo(dataUrl: string) {
  return apiRequest<ApiResponse<CompanyDetails>>("/company/logo", {
    method: "POST",
    body: JSON.stringify({ dataUrl }),
  });
}

export async function deleteCompanyLogo() {
  return apiRequest<ApiResponse<CompanyDetails>>("/company/logo", { method: "DELETE" });
}

// ============================================
// SCHEDULES API
// ============================================

export async function getSchedules(filter: ScheduleFilter) {
  return apiRequest<ApiResponse<DaySchedule[]>>(
    `/schedules?startDate=${filter.startDate}&endDate=${filter.endDate}`,
  );
}

/**
 * Get calendar availability (schedules + bookings) in a single optimized request
 */
export async function getCalendarAvailability(filter: ScheduleFilter) {
  return apiRequest<
    ApiResponse<{
      schedules: DaySchedule[];
      bookings: {
        id: string;
        date: string;
        timeSlot: string;
        tripId: string;
        status: string;
      }[];
    }>
  >(
    `/calendar/availability?startDate=${filter.startDate}&endDate=${filter.endDate}`,
  );
}

export async function getScheduleByDate(date: string) {
  return apiRequest<ApiResponse<DaySchedule>>(`/schedules/${date}`);
}

export async function upsertSchedule(schedule: {
  date: string;
  isAvailable: boolean;
  availableTrips: string[];
  notes?: string;
}) {
  return apiRequest<ApiResponse<DaySchedule>>("/schedules", {
    method: "PUT",
    body: JSON.stringify(schedule),
  });
}

export async function bulkCreateSchedules(request: {
  startDate: string;
  endDate: string;
  availableTrips: string[];
  notes?: string;
  isAvailable?: boolean;
}) {
  return apiRequest<ApiResponse<DaySchedule[]>>("/schedules/bulk", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

// ============================================
// ADMIN API
// ============================================

export async function getPartners() {
  return apiRequest<ApiResponse<Partner[]>>("/management/partners");
}

export async function getPartnerById(id: string) {
  return apiRequest<ApiResponse<Partner>>(`/management/partners/${id}`);
}

export async function updatePartnerStatus(id: string, isActive: boolean) {
  return apiRequest<ApiResponse<Partner>>(`/management/partners/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ isActive }),
  });
}

export async function createPartner(data: {
  name: string;
  domain: string;
  country: string;
  notes?: string;
  allowedEmails?: string[];
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  fortnoxId?: string;
  fortnoxCardPaymentCode?: string;
  loginEnabled?: boolean;
}) {
  return apiRequest<ApiResponse<Partner>>("/management/partners", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePartner(
  id: string,
  data: {
    name?: string;
    domain?: string;
    notes?: string;
    country?: string;
    loginEnabled?: boolean;
    allowedEmails?: string[];
    primaryContactName?: string;
    primaryContactEmail?: string;
    primaryContactPhone?: string;
    fortnoxId?: string;
    fortnoxCardPaymentCode?: string;
  },
) {
  return apiRequest<ApiResponse<Partner>>(`/management/partners/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ============================================
// ALLOWED DOMAINS API
// ============================================

export async function getAllowedDomains() {
  return apiRequest<ApiResponse<AllowedDomain[]>>("/management/domains");
}

export async function addAllowedDomain(
  domain: string,
  partnerId?: string,
  notes?: string,
) {
  const body: { domain: string; partnerId?: string; notes?: string } = {
    domain,
  };
  if (partnerId) body.partnerId = partnerId;
  if (notes) body.notes = notes;

  return apiRequest<ApiResponse<AllowedDomain>>("/management/domains", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function removeAllowedDomain(id: string) {
  return apiRequest<ApiResponse<void>>(`/management/domains/${id}`, {
    method: "DELETE",
  });
}

// ============================================
// USERS API
// ============================================

export async function getUsers(partnerId?: string) {
  const params = partnerId ? `?partnerId=${partnerId}` : "";
  return apiRequest<ApiResponse<User[]>>(`/management/users${params}`);
}

export async function getPartnerUsers(partnerId: string) {
  return apiRequest<ApiResponse<{ partner: Partner; users: User[] }>>(
    `/management/partners/${partnerId}/users`,
  );
}

export async function createPartnerUser(
  partnerId: string,
  email: string,
  name: string,
) {
  return apiRequest<ApiResponse<User>>(
    `/management/partners/${partnerId}/users`,
    {
      method: "POST",
      body: JSON.stringify({ email, name }),
    },
  );
}

export async function updateUserStatus(userId: string, isActive: boolean) {
  return apiRequest<ApiResponse<User>>(`/management/users/${userId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ isActive }),
  });
}

export async function updateUserRole(
  userId: string,
  role: "admin" | "partner",
) {
  return apiRequest<ApiResponse<User>>(`/management/users/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

// ============================================
// EMAIL TEMPLATES API
// ============================================

export async function getEmailTemplates() {
  return apiRequest<ApiResponse<EmailTemplate[]>>(
    "/management/email-templates",
  );
}

export async function getEmailTemplateById(id: string) {
  return apiRequest<ApiResponse<EmailTemplate>>(
    `/management/email-templates/${id}`,
  );
}

export async function createEmailTemplate(data: {
  name: string;
  type: EmailTemplateType;
  subject: string;
  htmlBody: string;
  isActive?: boolean;
  description?: string;
}) {
  return apiRequest<ApiResponse<EmailTemplate>>("/management/email-templates", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateEmailTemplate(
  id: string,
  data: Partial<EmailTemplate>,
) {
  return apiRequest<ApiResponse<EmailTemplate>>(
    `/management/email-templates/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function deleteEmailTemplate(id: string) {
  return apiRequest<ApiResponse<void>>(`/management/email-templates/${id}`, {
    method: "DELETE",
  });
}

// ============================================
// AUDIT LOGS API
// ============================================

import type {
  LoginAuditLog,
  DataAuditLog,
  EmailAuditLog,
  AuditLogFilter,
} from "@common/types";

export async function getLoginAuditLogs(filter?: AuditLogFilter) {
  const params = new URLSearchParams();
  if (filter?.userId) params.append("userId", filter.userId);
  if (filter?.userEmail) params.append("userEmail", filter.userEmail);
  if (filter?.startDate) params.append("startDate", filter.startDate);
  if (filter?.endDate) params.append("endDate", filter.endDate);
  if (filter?.success !== undefined)
    params.append("success", String(filter.success));

  const queryString = params.toString();
  return apiRequest<ApiResponse<LoginAuditLog[]>>(
    `/management/audit-logs/login${queryString ? `?${queryString}` : ""}`,
  );
}

export async function getDataAuditLogs(filter?: AuditLogFilter) {
  const params = new URLSearchParams();
  if (filter?.entityType) params.append("entityType", filter.entityType);
  if (filter?.entityId) params.append("entityId", filter.entityId);
  if (filter?.userId) params.append("userId", filter.userId);
  if (filter?.startDate) params.append("startDate", filter.startDate);
  if (filter?.endDate) params.append("endDate", filter.endDate);

  const queryString = params.toString();
  return apiRequest<ApiResponse<DataAuditLog[]>>(
    `/management/audit-logs/data${queryString ? `?${queryString}` : ""}`,
  );
}

export async function getEmailAuditLogs(filter?: AuditLogFilter) {
  const params = new URLSearchParams();
  if (filter?.startDate) params.append("startDate", filter.startDate);
  if (filter?.endDate) params.append("endDate", filter.endDate);

  const queryString = params.toString();
  return apiRequest<ApiResponse<EmailAuditLog[]>>(
    `/management/audit-logs/email${queryString ? `?${queryString}` : ""}`,
  );
}

// ============================================
// SHARE LINKS API
// ============================================

import type { ShareLink, CreateShareLinkRequest, PublicCalendarDay } from "@common/types";

export async function getShareLinks() {
  return apiRequest<ShareLink[]>("/management/share-links");
}

export async function createShareLink(data: CreateShareLinkRequest) {
  return apiRequest<ShareLink>("/management/share-links", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateShareLink(
  id: string,
  data: Partial<{ name: string; isActive: boolean; expiresAt: string; showTrips: boolean }>,
) {
  return apiRequest<ShareLink>(`/management/share-links/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteShareLink(id: string) {
  return apiRequest<void>(`/management/share-links/${id}`, {
    method: "DELETE",
  });
}

// ============================================
// FORTNOX INTEGRATION API
// ============================================

import type {
  CreateInvoiceFromBookingRequest,
  FortnoxInvoiceResponse,
} from "@common/types";

/**
 * Check Fortnox integration status
 */
export async function getFortnoxStatus() {
  return apiRequest<
    ApiResponse<{
      configured: boolean;
      message: string;
      companyInfo?: FortnoxCompanyInfo;
    }>
  >("/fortnox/status");
}

/**
 * Get list of articles from Fortnox
 */
export async function getFortnoxArticles() {
  return apiRequest<
    ApiResponse<
      Array<{
        ArticleNumber: string;
        Description: string;
        SalesPrice: number;
        Unit: string;
        VAT: number;
        Active?: boolean;
      }>
    >
  >("/fortnox/articles");
}

/**
 * Fortnox Customer type
 */
export interface FortnoxCustomer {
  CustomerNumber: string;
  Name: string;
  Email?: string;
  Phone1?: string;
  Address1?: string;
  Address2?: string;
  City?: string;
  ZipCode?: string;
  Country?: string;
  CountryCode?: string;
  OrganisationNumber?: string;
  Active: boolean;
  TermsOfPayment?: string; // "K" = kontant, "30" = 30-day invoice, etc. (from Fortnox detail endpoint)
}

/**
 * Get list of customers from Fortnox
 * Used for importing customers as partners
 */
export async function getFortnoxCustomers() {
  return apiRequest<ApiResponse<FortnoxCustomer[]>>("/fortnox/customers");
}

/**
 * Create a draft invoice from a booking
 */
export async function createFortnoxInvoice(
  request: CreateInvoiceFromBookingRequest,
) {
  return apiRequest<
    ApiResponse<{
      invoiceNumber: string;
      fortnoxDocumentNumber: string;
      booking: Booking;
      invoice: FortnoxInvoiceResponse;
    }>
  >("/fortnox/invoices", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Bulk refresh cached Fortnox invoice status for all invoiced bookings in a date range
 */
export async function syncFortnoxInvoiceStatus(
  startDate: string,
  endDate: string,
) {
  return apiRequest<ApiResponse<{ synced: number; failed: number }>>(
    "/fortnox/sync-invoice-status",
    {
      method: "POST",
      body: JSON.stringify({ startDate, endDate }),
    },
  );
}

/**
 * On-demand refresh of every Fortnox-linked partner's isActive flag from
 * its Fortnox customer's Active status (Fortnox is the source of truth).
 */
export async function syncFortnoxPartnerStatus() {
  return apiRequest<
    ApiResponse<{ deactivated: number; reactivated: number; failed: number }>
  >("/fortnox/sync-partner-status", {
    method: "POST",
  });
}

/**
 * Get invoice details from Fortnox
 */
export async function getFortnoxInvoice(documentNumber: string) {
  return apiRequest<ApiResponse<FortnoxInvoiceResponse>>(
    `/fortnox/invoices/${documentNumber}`,
  );
}

/**
 * Cancel a Fortnox invoice and revert booking to confirmed status
 */
export async function cancelFortnoxInvoice(
  documentNumber: string,
  bookingId: string,
  bookingDate: string,
) {
  return apiRequest<
    ApiResponse<{
      data: FortnoxInvoiceResponse;
      message: string;
    }>
  >(`/fortnox/invoices/${documentNumber}/cancel`, {
    method: "POST",
    body: JSON.stringify({ bookingId, bookingDate }),
  });
}

/**
 * Validate a Fortnox customer exists
 */
export async function validateFortnoxCustomer(customerNumber: string) {
  return apiRequest<
    ApiResponse<{ customerNumber: string; valid: boolean; message: string }>
  >("/fortnox/validate-customer", {
    method: "POST",
    body: JSON.stringify({ customerNumber }),
  });
}

// ============================================
// FORTNOX OAUTH API
// ============================================

/**
 * Get Fortnox company information
 * Validates connection and returns company details
 */
export async function getFortnoxCompanyInfo() {
  return apiRequest<{
    success: boolean;
    data: FortnoxCompanyInfo;
  }>("/fortnox/company");
}

/**
 * Disconnect Fortnox integration
 * Removes all tokens from Key Vault
 */
export async function disconnectFortnox() {
  return apiRequest<{
    success: boolean;
    message: string;
  }>("/fortnox/disconnect", {
    method: "POST",
  });
}

/**
 * Start Fortnox OAuth flow
 * Redirects browser to Fortnox authorization page
 * Note: We pass the token as a query parameter since browser redirects don't include Authorization headers
 */
export function startFortnoxOAuth() {
  const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";
  const token = useAuthStore.getState().token;
  if (!token) {
    throw new Error("Authentication required");
  }
  window.location.href = `${API_BASE_URL}/fortnox/auth/start?token=${encodeURIComponent(token)}`;
}

// ============================================
// FORTNOX SETTINGS API
// ============================================

export interface FortnoxSettings {
  lunchArticleNumber: string;
}

/**
 * Get Fortnox settings (lunch article, etc.)
 */
export async function getFortnoxSettings() {
  return apiRequest<ApiResponse<FortnoxSettings>>("/fortnox/settings");
}

/**
 * Update Fortnox settings
 */
export async function updateFortnoxSettings(
  settings: Partial<FortnoxSettings>,
) {
  return apiRequest<ApiResponse<FortnoxSettings>>("/fortnox/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

// ============================================
// ARTICLES API
// ============================================

import type { Article, ArticleSyncResult, UpdateArticleRequest } from "@common/types";

/**
 * Get all articles (admin sees all, others see only enabled)
 */
export async function getArticles() {
  return apiRequest<ApiResponse<Article[]>>("/articles");
}

/**
 * Get article by ID
 */
export async function getArticle(id: string) {
  return apiRequest<ApiResponse<Article>>(`/articles/${id}`);
}

/**
 * Update article enabled status
 */
export async function updateArticle(id: string, data: UpdateArticleRequest) {
  return apiRequest<ApiResponse<Article>>(`/articles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/**
 * Sync articles from Fortnox
 */
export async function syncArticles() {
  return apiRequest<ApiResponse<ArticleSyncResult> & { message: string }>(
    "/articles/sync",
    {
      method: "POST",
    },
  );
}

/**
 * Bulk update article enabled status
 */
export async function bulkUpdateArticles(
  updates: { id: string; isEnabled: boolean }[],
) {
  return apiRequest<ApiResponse<Article[]> & { message: string }>(
    "/articles/bulk",
    {
      method: "PATCH",
      body: JSON.stringify({ updates }),
    },
  );
}

// ============================================
// CONTACT SUBMISSIONS API (admin)
// ============================================

export async function getContactSubmissions(status?: string) {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiRequest<ApiResponse<ContactSubmission[]>>(
    `/contact-submissions${query}`,
  );
}

export async function updateContactStatus(
  id: string,
  status: "new" | "read" | "replied" | "archived",
  adminNotes?: string,
) {
  return apiRequest<ApiResponse<ContactSubmission>>(
    `/contact-submissions/${id}/status`,
    {
      method: "PATCH",
      body: JSON.stringify({ status, adminNotes }),
    },
  );
}

export async function generateContactSuggestion(id: string) {
  return apiRequest<ApiResponse<{ suggestion: string }>>(
    `/contact-submissions/${id}/suggest`,
    { method: "POST" },
  );
}

export async function sendContactReply(id: string, message: string) {
  return apiRequest<ApiResponse<null>>(
    `/contact-submissions/${id}/reply`,
    {
      method: "POST",
      body: JSON.stringify({ message }),
    },
  );
}

export async function deleteContactSubmission(id: string) {
  return apiRequest<ApiResponse<null>>(
    `/contact-submissions/${id}`,
    { method: "DELETE" },
  );
}

// ============================================
// STRIPE PAYMENT
// ============================================

import type { StripePaymentLinkResponse } from "@common/types";

export async function createStripePaymentLink(
  bookingId: string,
  bookingDate: string,
) {
  return apiRequest<ApiResponse<StripePaymentLinkResponse>>(
    "/stripe/payment-link",
    {
      method: "POST",
      body: JSON.stringify({ bookingId, bookingDate }),
    },
  );
}

// ============================================
// LOGBOOK
// ============================================
import type {
  Certificate as LogbookCertificate,
  ComplianceDeadline,
  CrewMember,
  CustomRule,
  Engine,
  EngineModel,
  EngineStatus,
  Drill,
  EquipmentItem,
  EquipmentServicePoint,
  JournalEntry,
  Vessel,
  Voyage,
} from "@common/types";

export type LogbookResource =
  | "vessels"
  | "crew"
  | "certificates"
  | "equipment"
  | "equipmentservicepoints"
  | "drills"
  | "customrules"
  | "enginemodels"
  | "engines"
  | "vesseldocuments";

export interface LogbookResourceMap {
  vessels: Vessel;
  crew: CrewMember;
  certificates: LogbookCertificate;
  equipment: EquipmentItem;
  equipmentservicepoints: EquipmentServicePoint;
  drills: Drill;
  customrules: CustomRule;
  enginemodels: EngineModel;
  engines: Engine;
  vesseldocuments: import("@common/types").VesselDocument;
}

export async function getLogbookList<R extends LogbookResource>(resource: R) {
  return apiRequest<ApiResponse<LogbookResourceMap[R][]>>(`/logbook/${resource}`);
}

export async function createLogbookItem(
  resource: LogbookResource,
  body: Record<string, unknown>,
) {
  return apiRequest<ApiResponse<unknown>>(`/logbook/${resource}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateLogbookItem(
  resource: LogbookResource,
  id: string,
  body: Record<string, unknown>,
) {
  return apiRequest<ApiResponse<unknown>>(`/logbook/${resource}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteLogbookItem(resource: LogbookResource, id: string) {
  return apiRequest<{ success: boolean }>(`/logbook/${resource}/${id}`, {
    method: "DELETE",
  });
}

export async function getLogbookVoyages(from?: string, to?: string) {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const qs = q.toString();
  return apiRequest<ApiResponse<Voyage[]>>(`/logbook/voyages${qs ? `?${qs}` : ""}`);
}

export async function getEkanSeason(vesselId: string, year: string, period?: { from: string; to: string }) {
  const q = new URLSearchParams({ vesselId, year, ...(period ?? {}) });
  return apiRequest<ApiResponse<import("@common/ekan").EkanSeason>>(`/logbook/ekan-season?${q}`);
}

export async function recordComplianceControl(body: import("@common/compliance-records").ComplianceRecord & { vesselId: string; note: string }) {
  return apiRequest<ApiResponse<JournalEntry>>("/logbook/control-records", {
    method: "POST", headers: { "x-operation-id": crypto.randomUUID() }, body: JSON.stringify(body),
  });
}

export type VoyageActionName = "confirm-count" | "depart" | "arrive" | "sign";

export async function runVoyageAction(
  id: string,
  action: VoyageActionName,
  body: { passengers?: number; crewCount?: number; crewIds?: string[]; engineHours?: number; engineHoursById?: Record<string, number | undefined>; arrivalPlace?: string; masterDecision?: string; masterCrewId?: string; hoursConfirm?: unknown } = {},
  operationId: string = crypto.randomUUID(),
) {
  return apiRequest<ApiResponse<Voyage>>(`/logbook/voyages/${id}/${action}`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "X-Operation-Id": operationId },
  });
}

export async function createVoyageDrafts() {
  return apiRequest<ApiResponse<{ created: number; skipped: number }>>(
    "/logbook/voyages/drafts",
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function getJournal(vesselId: string) {
  return apiRequest<{
    success: boolean;
    data: JournalEntry[];
    verification: { ok: boolean; brokenAt: string | null; entries: number };
  }>(`/logbook/journal?vesselId=${encodeURIComponent(vesselId)}`);
}

export async function backfillVoyage(body: {
  vesselId: string;
  departureAt?: string;
  arrivalAt?: string;
  tripName?: string;
  departurePlace?: string;
  passengers: number;
  crewCount: number;
  engineHoursStart?: number;
  engineHoursEnd?: number;
  note?: string;
  hoursConfirm?: unknown;
}) {
  return apiRequest<ApiResponse<Voyage>>("/logbook/voyages/backfill", { method: "POST", body: JSON.stringify(body) });
}

export async function getVoyageGuests(voyageId: string) {
  return apiRequest<ApiResponse<VoyageGuest[]>>(`/logbook/voyages/${voyageId}/guests`);
}

export async function getPunctuality(vesselId: string, from: string, to: string) {
  const q = new URLSearchParams({ vesselId, from, to });
  return apiRequest<ApiResponse<import("@common/punctuality").PunctualityReport>>(`/logbook/compliance/punctuality?${q.toString()}`);
}

export interface FleetRow {
  vesselId: string;
  name: string;
  isActive: boolean;
  evaluatedAt?: string;
  headline?: string;
  counts?: { action: number; soon: number; basis: number; ok: number; failedSources: number };
  openFaults?: number;
  failedSources?: string[];
  error?: string;
}

export async function getFleetOverview() {
  return apiRequest<ApiResponse<FleetRow[]>>("/logbook/compliance/fleet");
}

export async function resolveMapsLink(url: string) {
  return apiRequest<ApiResponse<{ ok: true; finalUrl: string; position: import("@common/destination").DestinationParse } | { ok: false; reason: string }>>("/maps/resolve", { method: "POST", body: JSON.stringify({ url }) });
}

export type RecountBody = {
  passengers: number;
  crewCount: number;
  crewIds?: string[];
  masterId?: string;
  reason: "boarding" | "alighting" | "crew_change" | "stop" | "correction" | "other";
  stop?: string;
  note?: string;
  basedOnVersion?: number;
  deviation?: string;
  occurredAt?: string;
};

/** A new count of the persons on board; made on the device when there is no connection and sent in order later */
export async function recountVoyage(voyageId: string, body: RecountBody) {
  return writeOrQueue<ApiResponse<Voyage>>({ kind: "note", label: `${body.passengers} + ${body.crewCount}`, endpoint: `/logbook/voyages/${voyageId}/recount`, body, groupKey: "journal" });
}

export async function recordCountReport(voyageId: string, body: { channel: "msw" | "other"; basis: "stated" | "receipt"; reference?: string; reportedAt?: string; note?: string }) {
  return writeOrQueue<ApiResponse<Voyage>>({ kind: "note", label: body.reference ?? body.basis, endpoint: `/logbook/voyages/${voyageId}/count-report`, body, groupKey: "journal" });
}

export async function getInspectionSummary(voyageId: string) {
  return apiRequest<ApiResponse<import("@common/inspection-summary").InspectionSummary>>(`/logbook/voyages/${voyageId}/inspection`);
}

export async function getVoyageForecast(voyageId: string, refresh = false) {
  return apiRequest<ApiResponse<import("@common/types").VoyageForecast>>(`/logbook/voyages/${voyageId}/forecast${refresh ? "?refresh=1" : ""}`);
}

export async function requestWeatherCompletion(voyageId: string, revise = false) {
  return apiRequest<ApiResponse<import("@common/types").WeatherJob>>(`/logbook/voyages/${voyageId}/weather/complete`, { method: "POST", body: JSON.stringify({ revise }) });
}

export async function getVoyageWeather(voyageId: string) {
  return apiRequest<ApiResponse<import("@common/types").VoyageWeather>>(`/logbook/voyages/${voyageId}/weather`);
}

export async function getWeatherReportPage(vesselId: string, from: string, to: string, after?: string) {
  const q = new URLSearchParams({ vesselId, from, to, limit: "100", ...(after ? { after } : {}) });
  return apiRequest<ApiResponse<import("@common/weather-report").WeatherReport>>(`/logbook/weather/report?${q.toString()}`);
}

/** The skipper's weather observation (or the correction of one); kept in the durable queue when there is no connection */
export async function addWeatherNote(voyageId: string, body: { visibilityNm?: number; seaState?: string; note?: string; occurredAt?: string; correctsEntryId?: string; reason?: string }) {
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "note", label: body.note?.slice(0, 60) ?? "weather", endpoint: `/logbook/voyages/${voyageId}/weather-note`, body, groupKey: "journal" });
}

export async function recordDrill(body: { vesselId: string; type: string; date: string; participants: string[]; notes?: string; occurredAt?: string }) {
  return writeOrQueue<ApiResponse<{ drill: unknown; entry: JournalEntry }>>({ kind: "drill", label: body.type, endpoint: "/logbook/drills/record", body, groupKey: "journal" });
}

export async function appendJournalEntry(body: {
  vesselId: string;
  voyageId?: string;
  type: "note" | "incident" | "check" | "bunkering" | "engine_hours" | "correction";
  text: string;
  correctsEntryId?: string;
  /** When it happened, if earlier than now */
  occurredAt?: string;
}) {
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "note", label: body.text.slice(0, 60), endpoint: "/logbook/journal", body, groupKey: "journal" });
}

export async function getEngineStatus() {
  return apiRequest<ApiResponse<EngineStatus[]>>("/logbook/engines/status");
}

export type EngineAction = "hours" | "service" | "check" | "fault";

export async function recordEngineAction(engineId: string, action: EngineAction, body: Record<string, unknown>) {
  const detail = typeof body.hours === "number" ? String(body.hours).replace(".", ",") : String(body.component ?? body.note ?? body.date ?? "");
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "engine", label: `${action} ${detail}`.trim(), endpoint: `/logbook/engines/${engineId}/${action}`, body, groupKey: `engine:${engineId}` });
}

export async function closeEngineFault(engineId: string, faultEntryId: string, note: string) {
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "engine", label: `close ${note.slice(0, 40)}`, endpoint: `/logbook/engines/${engineId}/fault/${faultEntryId}/close`, body: { note }, groupKey: `engine:${engineId}` });
}

export async function getCompliance() {
  return apiRequest<ApiResponse<ComplianceDeadline[]>>("/logbook/compliance");
}

// ============================================
// LOGBOOK RULES
// ============================================
import type { ResolvedRule } from "@common/types";

/** The rules; with a vessel id each rule also says whether it applies to that vessel. */
export async function getRules(vesselId?: string, scope?: "catalogue" | "company" | "operational") {
  const params = new URLSearchParams();
  if (vesselId) params.set("vesselId", vesselId);
  if (scope) params.set("scope", scope);
  const q = params.size ? `?${params}` : "";
  return apiRequest<ApiResponse<ResolvedRule[]>>(`/logbook/rules/current${q}`);
}

export async function setVesselRuleNotApplicable(vesselId: string, ruleId: string, reason: string) {
  return apiRequest<ApiResponse<unknown>>(`/logbook/vessels/${vesselId}/rules/${ruleId}`, {
    method: "PUT",
    body: JSON.stringify({ notApplicable: true, reason }),
  });
}

export async function removeVesselRuleDecision(vesselId: string, ruleId: string) {
  return apiRequest<{ success: boolean }>(`/logbook/vessels/${vesselId}/rules/${ruleId}`, {
    method: "DELETE",
  });
}

// ============================================
// LOGBOOK POSITIONS (AIS)
// ============================================
import type { VesselPosition } from "@common/types";

export async function getPositions(vesselId: string, from?: string, to?: string) {
  const q = new URLSearchParams({ vesselId });
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  return apiRequest<ApiResponse<VesselPosition[]>>(`/logbook/positions?${q.toString()}`);
}

export async function pollAisNow(seconds = 60) {
  return apiRequest<ApiResponse<{ seconds: number; vessels: number; heard: number; stored: number }>>("/logbook/ais/poll", {
    method: "POST",
    body: JSON.stringify({ seconds }),
  });
}

// ============================================
// LOGBOOK EVIDENCE (photos and PDFs)
// ============================================
import type { Evidence, EvidenceKind, VoyageGuest } from "@common/types";

/** Evidence as the browser sees it (the storage path is never sent). */
export type EvidenceItem = Omit<Evidence, "blobPath">;

export async function listEvidence(kind: EvidenceKind, refId: string) {
  const q = new URLSearchParams({ kind, refId });
  return apiRequest<ApiResponse<EvidenceItem[]>>(`/logbook/evidence?${q.toString()}`);
}

export async function uploadEvidence(kind: EvidenceKind, refId: string, file: Blob, fileName: string, note?: string) {
  const form = new FormData();
  form.set("kind", kind);
  form.set("refId", refId);
  if (note) form.set("note", note);
  form.set("file", file, fileName);
  return apiRequest<ApiResponse<EvidenceItem>>("/logbook/evidence", { method: "POST", body: form });
}

/** The file itself. It needs the login token, so it is fetched here and shown from a blob URL. */
export async function fetchEvidenceFile(id: string): Promise<Blob> {
  const token = useAuthStore.getState().token;
  const response = await fetch(`${API_BASE}/logbook/evidence/${id}/file`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 401) throw new Error("Session expired. Please login again.");
  if (!response.ok) {
    let message = `Could not open the file (${response.status})`;
    try {
      message = (await response.json()).error || message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  return response.blob();
}

// ============================================
// LOGBOOK FUEL (bunkering)
// ============================================
export interface BunkeringBody {
  vesselId: string;
  voyageId?: string;
  litres: number;
  pricePerLitre?: number;
  totalCost?: number;
  place?: string;
  supplier?: string;
  engineHours?: number;
  filledToFull: boolean;
  occurredAt?: string;
  hoursConfirm?: unknown;
  /** The level after the bunkering: percent of the tank and how it was found out (a full tank is filledToFull) */
  levelPercent?: number;
  levelSource?: "read" | "estimated" | "calculated";
}

/** A bunkering is saved as a structured journal entry: litres are a whole number. */
export async function getComplianceOverview(vesselId: string) {
  return apiRequest<ApiResponse<import("@common/compliance-overview").ComplianceOverview>>(`/logbook/compliance/overview?vesselId=${encodeURIComponent(vesselId)}`);
}

export async function getFuelForecast(vesselId: string) {
  return apiRequest<ApiResponse<import("@common/fuel-forecast").Forecast>>(`/logbook/fuel/forecast?vesselId=${encodeURIComponent(vesselId)}`);
}

export async function addFuelLevel(body: { vesselId: string; full?: boolean; levelPercent?: number; levelSource?: "read" | "estimated" | "calculated"; occurredAt?: string; note?: string }) {
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "fuel_level", label: body.full ? "100 %" : `${body.levelPercent ?? ""} %`, endpoint: "/logbook/fuel/level", body, groupKey: "fuel" });
}

export async function addBunkering(body: BunkeringBody) {
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "bunkering", label: `${body.litres} l`, endpoint: "/logbook/bunkering", body: body as unknown as Record<string, unknown>, groupKey: "fuel" });
}

// ============================================
// LOGBOOK CHECKLISTS AND CALENDAR
// ============================================
import type { ChecklistResult, VesselChecklist } from "@common/types";

export async function getAdoptions(vesselId: string) {
  return apiRequest<ApiResponse<VesselChecklist[]>>(`/logbook/checklists?vesselId=${encodeURIComponent(vesselId)}`);
}

/** The company adopts a checklist template for a vessel; until then it cannot be used. */
export async function adoptChecklist(vesselId: string, ruleId: string) {
  return apiRequest<ApiResponse<VesselChecklist>>(`/logbook/vessels/${vesselId}/checklists/${ruleId}`, { method: "PUT", body: JSON.stringify({}) });
}

export async function unadoptChecklist(vesselId: string, ruleId: string) {
  return apiRequest<{ success: boolean }>(`/logbook/vessels/${vesselId}/checklists/${ruleId}`, { method: "DELETE" });
}

export async function runChecklist(
  ruleId: string,
  body: { vesselId: string; voyageId?: string; items: { result: ChecklistResult; note?: string }[]; clientRevision?: number; reviewedWeatherSnapshotId?: string; title?: string },
) {
  const { title, ...payload } = body;
  return writeOrQueue<ApiResponse<JournalEntry>>({ kind: "checklist", label: title ?? ruleId, endpoint: `/logbook/checklists/${ruleId}/run`, body: payload as unknown as Record<string, unknown>, groupKey: `checklist:${ruleId}:${body.voyageId ?? body.vesselId}` });
}

/** A new secret link for the phone calendar (the old one stops working). */
export async function createCalendarLink() {
  return apiRequest<ApiResponse<{ url: string }>>("/logbook/calendar/token", { method: "POST", body: JSON.stringify({}) });
}

export async function revokeCalendarLink() {
  return apiRequest<{ success: boolean }>("/logbook/calendar/token", { method: "DELETE" });
}

/** The history of rule switches for a vessel (who, when, why, which version). */
export async function getRuleLog(vesselId: string, ruleId?: string) {
  const q = ruleId ? `?ruleId=${encodeURIComponent(ruleId)}` : "";
  return apiRequest<ApiResponse<import("@common/types").VesselRuleLog[]>>(`/logbook/vessels/${vesselId}/rule-log${q}`);
}

// ============================================
// RULE LIBRARY (owner)
// ============================================
import type { BuiltInRule, RuleVersion } from "@common/types";

export type LibraryRule = BuiltInRule & { version: number; publishedAt?: string; publishedBy?: string; changeNote?: string };

export async function getRuleLibrary() {
  return apiRequest<{ success: boolean; data: LibraryRule[]; imported: boolean; isOwner: boolean }>("/logbook/rules/library");
}

export async function importRuleLibrary(changeNote: string) {
  return apiRequest<ApiResponse<{ added: { ruleId: string; version: number }[]; unchanged: number; missingInFiles: string[] }>>("/logbook/rules/import", {
    method: "POST",
    body: JSON.stringify({ changeNote }),
  });
}

export async function getRuleVersions(ruleId: string) {
  return apiRequest<ApiResponse<RuleVersion[]>>(`/logbook/rules/${ruleId}/versions`);
}

export async function publishRule(ruleId: string, changeNote: string, changes: Record<string, unknown>) {
  return apiRequest<ApiResponse<{ ruleId: string; version: number; changed: string[] }>>(`/logbook/rules/${ruleId}/publish`, {
    method: "POST",
    body: JSON.stringify({ changeNote, changes }),
  });
}

export async function retireRule(ruleId: string, changeNote: string) {
  return apiRequest<ApiResponse<{ ruleId: string; version: number }>>(`/logbook/rules/${ruleId}/retire`, {
    method: "POST",
    body: JSON.stringify({ changeNote }),
  });
}

/** Public share-link calendar (no auth). `trips`/`slots` may be missing in old cached responses. */
export interface PublicCalendarResponse {
  linkName: string;
  showTrips?: boolean;
  calendar: PublicCalendarDay[];
}

export async function getPublicCalendar(
  token: string,
  startDate: string,
  endDate: string,
  errorMessages: { invalidOrExpired: string; failedToLoad: string },
): Promise<PublicCalendarResponse> {
  const base = import.meta.env.VITE_API_URL || "/api";
  const response = await fetch(
    `${base}/public/calendar/${token}?startDate=${startDate}&endDate=${endDate}`,
  );
  if (!response.ok) {
    throw new Error(
      response.status === 404 ? errorMessages.invalidOrExpired : errorMessages.failedToLoad,
    );
  }
  return response.json();
}
